import styles from "./QuoteCard.module.scss";
import { Button, Text } from "@ilc-technology/luik";
import { PaymentInfoStatus, QuotePaymentStatus, SalesItemType, SegmentType } from "../../../Common/Types";
import Enrich from "../../../Common/services/TextEnricher";
import { useDatasources } from "../../../contexts/StoryblokContext/StoryblokContext";
import { LabelKey } from "../../../Common/StoryblokTypes";
import { QuoteData, useQuoteContext } from "../../../contexts/QuoteContext/QuoteContext";
import React, { useMemo, useState } from "react";
import { AppConfig } from "../../../AppConfig";
import parse from "html-react-parser";
import { useNavigate } from "react-router-dom";
import { addDays, calculateDaysUntilExpiration, formatDate } from "../../../Common/Helpers/DateHelper";
import { useSessionContext } from "../../../contexts/SessionContext/SessionContext";
import { ContentType, StandardTrackingEvent, trackEvent, QuoteEventType } from "../../../Common/services/Analytics";
import {
  getPaymentOrReservation,
  isQuoteFullyPaid as isQuoteFullyPaidByPayments,
} from "../../../Common/services/PaymentsService";
import TBCardStatus, { TBCardStatusAlignment, TBCardStatusPosition } from "../../BaseComponents/TBCardStatus";
import { ColorVariant } from "../../../Common/services/ColorService";
import { getDestinationName } from "../../../Common/services/QuoteService";
import { TRUE } from "../../../Common/Constants";
import PreferredQuoteToggle from "./PreferredQuoteToggle";
import StarOutlineIcon from "../../../assets/icons/StarOutline.svg";
import StarIcon from "../../../assets/icons/Star.svg";

interface QuoteCardProps {
  quote: QuoteData;
  isPreferred?: boolean;
  onSetPreferred?: () => void;
}

const QuoteCard: React.FC<QuoteCardProps> = ({ quote, isPreferred = false, onSetPreferred }) => {
  const { language } = useSessionContext();
  const { labels, featureSettings } = useDatasources();
  const { quotesContentCache, isQuotesDestinationsLoading, sendQuoteEvent } = useQuoteContext();
  const { id, segments, payments } = quote.quoteData;
  const quoteUrl = !id ? "" : `/quote/${id}`;
  const courseSegments = quote.quoteData.segments.filter((segment) => segment.type === SegmentType.Course);
  const firstDestinationCode = courseSegments[0].destinationCode;
  const isDaysRemainingEnabled = featureSettings?.isDaysRemainingEnabled === TRUE;
  const [isOutlineStarHovered, setIsOutlineStarHovered] = useState(false);

  const allDestinationNames = useMemo(
    () =>
      !isQuotesDestinationsLoading &&
      courseSegments.map((segment) => quotesContentCache.destinations.get(segment.destinationCode)?.name),
    [isQuotesDestinationsLoading]
  );

  const destinationLabel = useMemo(
    () =>
      !isQuotesDestinationsLoading && allDestinationNames
        ? getDestinationName(courseSegments, quotesContentCache.destinations, " & ")
        : "",
    [isQuotesDestinationsLoading, allDestinationNames]
  );

  const bannerImage = useMemo(
    () =>
      !isQuotesDestinationsLoading
        ? quotesContentCache.destinations.get(firstDestinationCode)?.heroImage ?? AppConfig.content.defaultBannerImage
        : "",
    [isQuotesDestinationsLoading]
  );

  const navigate = useNavigate();

  const startDate = formatDate(segments[0].startAt, language, true);
  const endDate = formatDate(segments[segments.length - 1].endAt, language, true);

  const daysUntilExpiration = calculateDaysUntilExpiration(quote.quoteData.validUpTo);

  const courses = segments
    .filter((s) => s.type === SegmentType.Course)!
    .map((s) => s.lineItems.find((li) => li.type === SalesItemType.Course));
  const courseDescriptions = courses.map((s) => s?.description).join(" & ");
  const courseDuration = courses.reduce((acc, val) => acc + (val?.quantity ?? 0), 0);
  const weeksLabel =
    courseDuration === 1
      ? labels[LabelKey.durationWeek]
      : courseDuration > 4
        ? labels[LabelKey.duration5orMoreWeeks]
        : labels[LabelKey.duration2to4Weeks];

  const isQuotePaidOrReserved =
    payments && payments.find((x) => x.status === PaymentInfoStatus.Reserve || x.status === PaymentInfoStatus.Paid);
  const payment = getPaymentOrReservation(payments);
  const isQuoteFullyPaid = isQuoteFullyPaidByPayments(payments);
  const isQuoteCanceled = quote.quoteData.status === QuotePaymentStatus.Canceled;

  const paymentMadeAt = payment?.madeAt
    ? payment?.status === PaymentInfoStatus.Paid
      ? formatDate(payment?.madeAt, language)
      : formatDate(addDays(new Date(payment?.madeAt), 1).toString(), language)
    : undefined;

  const enricherContext = isQuotePaidOrReserved
    ? {
        currentQuote: quote,
        paymentStatus: payment?.status,
        paymentMadeAt: paymentMadeAt,
      }
    : {
        currentQuote: quote,
        paymentStatus: "",
        paymentMadeAt: undefined,
      };
  const paymentInfoLabel = "paymentInfo_";
  const isPreferredQuoteVisible = !isQuoteFullyPaid && (isPreferred || Boolean(onSetPreferred));

  const isMultiQuote = courses.length != 1;
  const bgColorStyle =
    isQuotePaidOrReserved && payment?.status == PaymentInfoStatus.Reserve ? ColorVariant.Yellow : ColorVariant.Green;
  const showDaysRemainingBadge =
    isDaysRemainingEnabled && daysUntilExpiration !== null && daysUntilExpiration >= 0 && !isQuoteFullyPaid;
  const showDaysRemainingOnImage = showDaysRemainingBadge && isPreferredQuoteVisible;

  const handleLinkClick = (quoteUrl: string) => {
    sendQuoteEvent(id, QuoteEventType.QuoteSelected);
    trackEvent(quote.quoteData.opportunityUuid, StandardTrackingEvent.SelectContent, {
      content_type: ContentType.QuoteDetails,
      content_id: id,
    });
    navigate(quoteUrl);
  };

  const handleSetPreferred = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setIsOutlineStarHovered(false);
    onSetPreferred?.();
  };

  return (
    <div
      data-testid="quote-card"
      onClick={() => handleLinkClick(quoteUrl)}
      className={`a-hover-scale relative ${isQuoteCanceled ? "pointer-events-none" : ""}`}
      aria-disabled={isQuoteCanceled}
    >
      {isQuotePaidOrReserved && (
        <TBCardStatus
          position={TBCardStatusPosition.OverCard}
          variant={bgColorStyle}
          alignment={TBCardStatusAlignment.Center}
          size="sm"
          testId="quote-card-status-payment"
        >
          <Text variant="label-sm-bold">{Enrich(labels[paymentInfoLabel + payment?.status], enricherContext)}</Text>
        </TBCardStatus>
      )}
      <div
        className={`a-list-card-h ${isQuoteCanceled ? "" : "shadow-card"} ${isQuotePaidOrReserved ? "rounded-b-lg" : isPreferred && !isQuoteFullyPaid ? "lg:a-rounded rounded-b-lg" : "a-rounded"} ${styles.containerGrid}`}
      >
        <div className={`${styles.firstColumn} relative`}>
          {bannerImage ? (
            <img
              style={{ filter: isQuoteCanceled ? "grayscale(100%) opacity(25%)" : "none" }}
              className={styles.destinationImage}
              src={bannerImage}
            />
          ) : (
            <div
              style={{ filter: isQuoteCanceled ? "grayscale(100%) opacity(25%)" : "none" }}
              className={styles.destinationImage}
            />
          )}
          {showDaysRemainingOnImage && (
            <div className="absolute left-2 top-2 z-10 rounded border-2 border-warning bg-white px-3 py-2 text-sm font-bold leading-5 lg:hidden">
              <span className="text-warning">
                {labels[LabelKey.daysRemaining]}: {daysUntilExpiration}
              </span>
            </div>
          )}
          {isQuoteCanceled && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-full text-center">
                <span className="inline-block rounded-[5px] bg-white px-4 py-2 text-[1rem] font-bold text-danger">
                  {labels[LabelKey.quoteStatus_Canceled] ?? "Expired"}
                </span>
              </div>
            </div>
          )}
        </div>
        <div
          className={styles.secondColumn}
          style={{ filter: isQuoteCanceled ? "grayscale(100%) opacity(25%)" : "none" }}
        >
          <div className={styles.durationRow}>
            <label className={`${styles.duration} text-gray-45`}>
              {startDate} - {endDate} | {Enrich(weeksLabel, { numWeeks: courseDuration }).toUpperCase()}
            </label>
            {isPreferredQuoteVisible && (
              <div data-testid="quote-card-status-preferred" className={styles.preferredToggle}>
                <PreferredQuoteToggle
                  isPreferred={isPreferred}
                  isOutlineStarHovered={isOutlineStarHovered}
                  preferredTooltipText={labels[LabelKey.preferredTooltip]}
                  preferredQuoteLabel={labels[LabelKey.preferredQuote]}
                  setAsPreferredLabel={labels[LabelKey.setAsPreferred]}
                  onHoverChange={setIsOutlineStarHovered}
                  onClick={handleSetPreferred}
                />
              </div>
            )}
          </div>
          <div className={styles.courseInformation}>
            <label className={`${styles.duration} text-gray-45`}>
              {startDate} - {endDate} | {Enrich(weeksLabel, { numWeeks: courseDuration }).toUpperCase()}
            </label>
            <h3 className={styles.destination}>{destinationLabel}</h3>
            {!isMultiQuote && <Text variant="paragraph-small-body">{courseDescriptions}</Text>}
          </div>
          <div className={styles.priceInformation}>
            <Text variant="label-lg-bold" as="p">
              {parse(
                Enrich(
                  labels[LabelKey.quotePriceTotal]?.replace(/(\{quoteTotalPrice\})/g, "<nobr>$1</nobr>"),
                  enricherContext
                )
              )}
            </Text>
            <Text variant="label-sm-bold" className="text-gray-45" as="p">
              {Enrich(labels[LabelKey.quotePricePerWeek], enricherContext)}
            </Text>
          </div>
          <div className={`${styles.quoteButton} ${styles.quoteButtonPreferredMobile}`}>
            <div className={`${styles.quoteButtonActionRow} flex w-full items-center justify-between px-4`}>
              <Button
                size="sm"
                intent="secondary-black"
                onPress={() => {
                  handleLinkClick(quoteUrl);
                }}
              >
                {labels[LabelKey.showDetails]}
              </Button>
              {isPreferredQuoteVisible && (
                <div className="inline-flex items-center gap-2 lg:hidden">
                  {isPreferred ? (
                    <>
                      <Text variant="label-sm-bold">{labels[LabelKey.preferredQuote]}</Text>
                      <img src={StarIcon} alt="" aria-hidden="true" className="size-6 shrink-0" />
                    </>
                  ) : (
                    <button
                      type="button"
                      aria-label={labels[LabelKey.setAsPreferred]}
                      className="inline-flex size-6 cursor-pointer items-center justify-center"
                      onClick={handleSetPreferred}
                    >
                      <img src={StarOutlineIcon} alt="" aria-hidden="true" className="pointer-events-none size-6" />
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
          {!isQuoteFullyPaid && (
            <div className={`${styles.quoteValidityInformation} text-gray-45`}>
              <Text variant="paragraph-caption" className="text-gray-45" as="p">
                {Enrich(labels[LabelKey.quoteValidUntil], {
                  quoteValidToDate: formatDate(quote?.quoteData?.validUpTo, language, true),
                })}
              </Text>
            </div>
          )}
          {showDaysRemainingBadge && !showDaysRemainingOnImage && (
            <div className="absolute bottom-2.5 right-2.5 z-10 rounded border-2 border-warning bg-white px-3 py-2 text-sm font-bold leading-5 md:bottom-2.5 md:right-2.5 lg:bottom-2.5 lg:right-2.5">
              <span className="text-warning">
                {labels[LabelKey.daysRemaining]}: {daysUntilExpiration}
              </span>
            </div>
          )}
          {showDaysRemainingOnImage && (
            <div className="absolute bottom-2.5 right-2.5 z-10 hidden rounded border-2 border-warning bg-white px-3 py-2 text-sm font-bold leading-5 lg:block">
              <span className="text-warning">
                {labels[LabelKey.daysRemaining]}: {daysUntilExpiration}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuoteCard;
